import { useTranslation } from 'react-i18next';
import type { ProductionCostClosingDetailLine } from '@/utils/approval/productionCostClosing';
import { formatNumber, text, toNumber } from './content';

const SHOWS_SUBCATEGORY = new Set(['3', '4', '6']);
const SHOWS_FIRST_QUOTE = new Set(['2', '5']);

/**
 * 레거시 appr_sub_view_forms_tax_cust_deatail_view_mstr — 행의 구분(division)에 따라 열이 다름.
 * 3·4·6 은 중분류, 2·5 는 최초 협의가(결제단가와 다르면 강조)
 */
const ProductionCostClosingDetailTable = ({
  lines,
  division,
  maker,
}: {
  lines: ProductionCostClosingDetailLine[];
  division: string;
  maker: string;
}) => {
  const { t } = useTranslation();
  const showsSubcategory = SHOWS_SUBCATEGORY.has(division);
  const showsFirstQuote = SHOWS_FIRST_QUOTE.has(division);
  const leadCols = showsSubcategory ? 6 : 5;
  let count = 0;
  let quoteSum = 0;
  return (
    <table className="detail-lines">
      <tbody>
        <tr>
          <th className="text-center" rowSpan={2}>
            {t('업체코드')}
          </th>
          <th className="text-center" rowSpan={2}>
            {t('업체명')}
          </th>
          <th className="text-center" rowSpan={2}>
            BL
          </th>
          {showsSubcategory && (
            <th className="text-center" rowSpan={2}>
              {t('중분류')}
            </th>
          )}
          <th className="text-center" rowSpan={2}>
            {division === '3' ? t('품목') : t('스타일번호')}
          </th>
          <th className="text-center" rowSpan={2}>
            {t('지급일자')}
          </th>
          <th className="text-center" rowSpan={2}>
            {t('결제수량')}
          </th>
          {showsFirstQuote && (
            <th className="text-center" rowSpan={2}>
              {t('최초 협의가')}
            </th>
          )}
          <th className="text-center" rowSpan={2}>
            {t('화폐 단위')}
          </th>
          <th className="text-center" rowSpan={2}>
            {t('결제단가')}
          </th>
          <th className="text-center" colSpan={2}>
            {t('결제금액')}
          </th>
          <th className="text-center" rowSpan={2}>
            VAT
          </th>
          <th className="text-center" rowSpan={2}>
            {t('합계')}
          </th>
        </tr>
        <tr>
          <th className="text-center">{t('화폐')}</th>
          <th className="text-center">{t('원화')}</th>
        </tr>
        {lines.map((line, i) => {
          const sum = toNumber(line.enterAmt) + toNumber(line.vat);
          if (text(line.chk) === '1') {
            count += 1;
            quoteSum += toNumber(line.quot);
            const changed =
              showsFirstQuote && text(line.amtUnit) !== text(line.quot);
            return (
              <tr key={i}>
                <td className="text-center">{`${division}${maker}`}</td>
                <td className="text-center">{text(line.custName)}</td>
                <td className="text-center">{text(line.blNo)}</td>
                {showsSubcategory && (
                  <td className="text-center">{text(line.divisionName)}</td>
                )}
                <td className="text-center">{text(line.styleNo)}</td>
                <td className="text-center">{text(line.settlementYmd)}</td>
                <td className="text-right">{formatNumber(line.enterQty)}</td>
                {showsFirstQuote && (
                  <td className="text-right">{formatNumber(line.quot)}</td>
                )}
                <td className="text-center">{text(line.cur)}</td>
                <td className={`text-right ${changed ? 'bg-danger' : ''}`}>
                  {formatNumber(line.amtUnit)}
                </td>
                <td className="text-right">{formatNumber(line.grAmtCurr)}</td>
                <td className="text-right">{formatNumber(line.enterAmt)}</td>
                <td className="text-right">{formatNumber(line.vat)}</td>
                <td className="text-right">{formatNumber(sum)}</td>
              </tr>
            );
          }
          const totalCount = count;
          const totalQuote = quoteSum;
          count = 0;
          quoteSum = 0;
          return (
            <tr key={i} className="bg-success textBold">
              <td className="text-center" colSpan={leadCols}>
                {t('합계 [ 총{{count}}건]', { count: totalCount })}
              </td>
              <td className="text-right">{formatNumber(line.enterQty)}</td>
              {showsFirstQuote && (
                <td className="text-right">{formatNumber(totalQuote)}</td>
              )}
              <td />
              <td className="text-right">{formatNumber(line.amtUnit)}</td>
              <td className="text-right">{formatNumber(line.grAmtCurr)}</td>
              <td className="text-right">{formatNumber(line.enterAmt)}</td>
              <td className="text-right">{formatNumber(line.vat)}</td>
              <td className="text-right">{formatNumber(sum)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ProductionCostClosingDetailTable;
